import { FaInfoCircle, FaTrashAlt } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import type { StrategyCoverage } from './backtestStatusTypes';
import { formatStrategyVersionKey } from './backtestStatusFormatters';

interface PreviousVersionBacktestsNoticeProps {
    canManage: boolean;
    coverage: StrategyCoverage;
    isLoading: boolean;
    onReviewCleanup: () => void;
}

const PreviousVersionBacktestsNotice = ({ canManage, coverage, isLoading, onReviewCleanup }: PreviousVersionBacktestsNoticeProps) => {
    const previousVersions = coverage.backtestVersionKeySummary.otherKeys;

    if (previousVersions.length === 0) return null;

    const previousBacktestsCount = previousVersions.reduce((sum, version) => sum + version.count, 0);

    return (
        <div className="rounded-lg border border-blue-700/50 bg-blue-950/20 p-4 text-sm text-blue-100">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 gap-3">
                    <FaInfoCircle aria-hidden="true" className="mt-0.5 shrink-0 text-blue-300" />
                    <div className="space-y-2">
                        <div>
                            <span className="font-semibold">
                                {previousBacktestsCount.toLocaleString()} previous-version backtest
                                {previousBacktestsCount === 1 ? '' : 's'}
                            </span>{' '}
                            across {previousVersions.length} version{previousVersions.length === 1 ? '' : 's'} are excluded from coverage
                            and performance statistics.
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-blue-200/70">
                            {previousVersions.slice(0, 3).map((version) => (
                                <span key={version.strategyVersionKey ?? 'legacy-null'}>
                                    <span className="font-mono">{formatStrategyVersionKey(version.strategyVersionKey)}</span> ·{' '}
                                    {version.count.toLocaleString()} backtest{version.count === 1 ? '' : 's'}
                                    {version.lastUpdateDateTime ? ` · ${version.lastUpdateDateTime}` : ''}
                                </span>
                            ))}
                            {previousVersions.length > 3 && <span>+{previousVersions.length - 3} more versions</span>}
                        </div>
                    </div>
                </div>
                {canManage && (
                    <Button
                        icon={<FaTrashAlt aria-hidden="true" />}
                        isLoading={isLoading}
                        size="sm"
                        variant="secondary"
                        onClick={onReviewCleanup}
                    >
                        Review &amp; delete
                    </Button>
                )}
            </div>
        </div>
    );
};

export default PreviousVersionBacktestsNotice;
